import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { parseDate } from '@internationalized/date';
import { createFileRoute } from '@tanstack/react-router';
import { useFlatData } from '@interxfi/api-client';
import { useIxcSweepsClearingV1SweepParticipantQueryServiceSweepParticipantGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-clearing-v1-sweep-participant-query-service-sweep-participant-get';
import { useIxcSweepsClearingV1SweepParticipantStatsQueryServiceSweepParticipantStatsList } from '@interxfi/api-client/generated/hooks/ixc-sweeps-clearing-v1-sweep-participant-stats-query-service-sweep-participant-stats-list';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { SelectInput } from '@interxfi/design-system/components/input';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import { usePreferredTimeZone } from '@interxfi/design-system/time-zone-context';
import { useCurrentDate } from '@interxfi/design-system/util';
import {
  type IxcSweepsClearingV1SweepParticipantState,
  IxcSweepsClearingV1SweepParticipantStatsQueryServiceSweepParticipantStatsListFilterableFields,
  type IxcSweepsClearingV1SweepParticipantStatsQueryServiceSweepParticipantStatsListSweepParticipantStatsListRequest,
  type IxcSweepsClearingV1SweepParticipantStatsState,
} from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { getDateFormatter } from '@interxfi/utilities/date/formatting';
import { SweepParticipantStatsEntityMenu } from '../../../../../../../../../../features/sweeps/clearing/participant/stats/sweep-participant-stats-entity-menu';
import { getSweepParticipantStatsStateListItems } from '../../../../../../../../../../features/sweeps/clearing/participant/stats/sweep-participant-stats-list-items';
import { SweepParticipantsPaymentsTable } from '../../../../../../../../../../features/sweeps/clearing/participant/stats/sweep-participant-stats-payments-table';
import {
  SweepParticipantEventDrawer,
  SweepParticipantEventSummary,
} from '../../../../../../../../../../features/sweeps/clearing/participant/sweep-participant-event';
import { getSweepParticipantStateListItems } from '../../../../../../../../../../features/sweeps/clearing/participant/sweep-participant-list-items';
import { getSweepsParticipantDetailPath } from '../../../../../../../../../../features/sweeps/clearing/participant/sweep-participant-routing';
import { SweepParticipantStatusPill } from '../../../../../../../../../../features/sweeps/clearing/participant/sweep-participant-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/sweep/participant/_participant/$accountId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `sweep-participant-${params.accountId}`,
        label: 'Participant',
        to: getSweepsParticipantDetailPath(params.accountId),
        suffix: <CopyButton content={params.accountId} label="Copy Account ID" tooltip={{ copy: params.accountId, copied: 'Copied Account ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Sweep Participant'),
  component: SweepParticipantDetail,
});

const monthFormatter = getDateFormatter('en-US', {
  month: 'long',
  year: 'numeric',
});

function getListItems(data: IxcSweepsClearingV1SweepParticipantState | undefined) {
  const listItems = getSweepParticipantStateListItems(data);

  return {
    general: {
      left: [listItems.data.sweepPlanId] satisfies ReadonlyListItem[],
      right: [listItems.accountId, listItems.data.updateTimestamp] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(accountId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: SweepParticipantEventDrawer,
        props: { request: { accountId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load sweep participant', 'view this sweep participant');

function getSweepParticipantStatsRequest(accountId: string) {
  return {
    query: {
      filters: [
        {
          field: {
            name: IxcSweepsClearingV1SweepParticipantStatsQueryServiceSweepParticipantStatsListFilterableFields.AccountId,
            type: {
              value: accountId,
            },
          },
        },
      ],
    },
  } as const satisfies IxcSweepsClearingV1SweepParticipantStatsQueryServiceSweepParticipantStatsListSweepParticipantStatsListRequest;
}

function getSweepParticipantStatItems(data: IxcSweepsClearingV1SweepParticipantStatsState | undefined) {
  const items = getSweepParticipantStatsStateListItems(data);

  return [items.data.sweptBalance, items.data.dividends, items.data.dividendDate, items.data.expectedPaymentDate];
}

function SweepParticipantDetail() {
  const { accountId } = Route.useParams();
  const request = { accountId } as const;
  const { data, error, isPending } = useIxcSweepsClearingV1SweepParticipantQueryServiceSweepParticipantGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.sweepParticipant), [data?.sweepParticipant]);
  const menuItems = useMemo(() => getMenuItems(accountId), [accountId]);
  const timeZone = usePreferredTimeZone();

  const sweepParticipantListRequest = useMemo(() => getSweepParticipantStatsRequest(accountId), [accountId]);
  const {
    data: sweepParticipantData,
    isPending: sweepParticipantStatsPending,
    error: sweepParticipantStatsError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useIxcSweepsClearingV1SweepParticipantStatsQueryServiceSweepParticipantStatsList(sweepParticipantListRequest);
  const showSweepParticipantStatsPending = Boolean(sweepParticipantStatsPending || sweepParticipantStatsError);
  const [selectedStats, setSelectedStats] = useState<IxcSweepsClearingV1SweepParticipantStatsState | undefined>();
  const statsListItems = useMemo(() => getSweepParticipantStatItems(selectedStats), [selectedStats]);
  const flatSweepStats = useFlatData(sweepParticipantData, 'sweepParticipantStats');

  useEffect(() => {
    if (flatSweepStats.length) {
      setSelectedStats((prev) => {
        if (prev) {
          return prev;
        }

        return flatSweepStats[0];
      });
    }
  }, [flatSweepStats]);

  const currentDate = useCurrentDate();
  const currentMonthStart = useMemo(() => {
    currentDate.date.set({ day: 1 });
    return currentDate.date;
  }, [currentDate]);

  const statMonthOptions: InputDropdownOption[] = useMemo(() => {
    if (!flatSweepStats.length) {
      return [
        {
          label: monthFormatter.format(currentMonthStart.toDate(timeZone)),
          value: currentMonthStart.toString(),
        },
      ];
    }

    return flatSweepStats.map((stat) => {
      const firstDayOfMonth = stat.firstDayOfMonth;
      const date = parseDate(firstDayOfMonth);

      return {
        label: monthFormatter.format(date.toDate(timeZone)),
        value: firstDayOfMonth,
      };
    });
  }, [currentMonthStart, flatSweepStats, timeZone]);

  const renderedMonth = selectedStats?.firstDayOfMonth ?? currentMonthStart.toString();

  const handleSelectedStatChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const matchingStat = flatSweepStats.find((stat) => stat.firstDayOfMonth === e.target.value);

      if (matchingStat) {
        setSelectedStats(matchingStat);
      }
    },
    [flatSweepStats],
  );

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <SweepParticipantStatusPill isPending={showPending} size="large" status={data?.sweepParticipant?.status} />
            </HorizontalStack>

            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <SweepParticipantEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={7}>
          <VerticalStack gap={4}>
            <HorizontalStack align="space-between" blockAlign="center" gap={2} wrap="nowrap">
              <Typography.Level3>Statistics</Typography.Level3>

              <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                <SelectInput
                  allowClear={false}
                  hideLabel
                  label="Select Month"
                  name="select-month"
                  onChange={handleSelectedStatChange}
                  options={statMonthOptions}
                  pagination={{ hasNextPage, fetchNextPage, isFetchingNextPage }}
                  placement="bottom-end"
                  value={renderedMonth}
                  variant="button-light-small"
                />

                <SweepParticipantStatsEntityMenu accountId={accountId} firstDayOfMonth={renderedMonth} sweepTargetId={selectedStats?.sweepTargetId} />
              </HorizontalStack>
            </HorizontalStack>

            <AppReadonlyList isPending={showSweepParticipantStatsPending} items={statsListItems} />
          </VerticalStack>

          <VerticalStack gap={4}>
            <Typography.Level3>Payments</Typography.Level3>

            <SweepParticipantsPaymentsTable
              accountId={accountId}
              data={selectedStats?.data?.payments ?? STABLE_EMPTY_ARRAY_REF}
              startDate={renderedMonth}
            />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
